<template>
  <div class="card-page">
    <!-- 背景装饰 -->
    <div class="bg-gradient"></div>
    <div class="orb orb-1"></div>
    <div class="orb orb-2"></div>
    <div class="orb orb-3"></div>

    <div class="content-wrapper">
      <aside class="sidebar">
        <nav class="nav-card">
          <div class="nav-logo">
            <div class="logo-icon">
              <img :src="logo2Image" alt="" class="logo2-icon" />
            </div>
            <div class="brand-copy">
              <h1 class="brand-name">树苗英语</h1>
              <span class="brand-subtitle">TREE SAPLING</span>
            </div>
          </div>

          <ul class="nav-list">
            <li v-for="item in navItems" :key="item.id">
              <a
                href="#"
                @click.prevent="handleNavItemClick(item)"
                :class="['nav-item', { active: activeNav === item.id }]"
              >
                <font-awesome-icon :icon="item.icon" class="nav-icon" />
                <span>{{ item.label }}</span>
              </a>
            </li>
          </ul>
          <div class="nav-menu-visual" aria-hidden="true">
            <img :src="menuBgImage" alt="" class="nav-menu-image" />
          </div>
        </nav>
      </aside>

      <!-- 主内容 -->
      <main class="main-content">
        <!-- 1️⃣ 顶部 Header -->
        <header class="page-header" :class="{ 'is-scrolled': isHeaderScrolled }">
          <button type="button" class="mobile-back-btn" @click="handleGoBack" aria-label="返回上一页">
            <font-awesome-icon icon="chevron-left" />
          </button>
          <h1 class="main-title">
            {{ pageTitle }}
          </h1>
        </header>
        <div class="page-header-spacer" aria-hidden="true"></div>

        <!-- 3️⃣ 卡片列表区 -->
        <div class="cards-grid">
          <div
            v-for="(card, index) in filteredCards"
            :key="card.id"
            class="word-card"
            :class="{ playing: currentPlayingIndex === index }"
            @click="handleCardClick(card, index)"
            :ref="el => { if (isAutoPlaying && currentPlayingIndex === index) scrollToCard(el) }"
          >
            <img v-if="card.coverUrl" :src="card.coverUrl" alt="" class="word-cover" />
          </div>
        </div>
      </main>
    </div>

    <!-- 5️⃣ 底部 Dock 控制栏 -->
    <div class="dock-bar">
      <button class="dock-item" :class="{ active: !showTranslation }" @click="toggleTranslation">
        <font-awesome-icon icon="language" />
        <span>关闭翻译</span>
      </button>

      <button class="dock-item" @click="goToFullscreen">
        <font-awesome-icon icon="expand" />
        <span>全屏模式</span>
      </button>

      <button class="dock-item main-btn" @click="toggleAutoPlay">
        <font-awesome-icon :icon="isAutoPlaying ? 'pause' : 'play'" />
      </button>

      <button class="dock-item" @click="openSettings">
        <font-awesome-icon icon="cog" />
        <span>显示设置</span>
      </button>

      <button class="dock-item" @click="openTestMode">
        <font-awesome-icon icon="clipboard-check" />
        <span>测试练习</span>
      </button>
    </div>

    <!-- 4️⃣ 全屏沉浸模式 -->
    <Transition name="fullscreen">
      <div
        v-if="fullscreenCard"
        class="fullscreen-overlay"
        @click.self="closeFullscreen"
      >
        <div class="fullscreen-card">
          <!-- 背景模糊层 -->
          <div class="fullscreen-bg" :style="{ background: fullscreenCard.bgColor }"></div>

          <!-- 关闭按钮 -->
          <button class="close-btn" @click="closeFullscreen">
            <font-awesome-icon icon="times" />
          </button>

          <!-- 内容区 -->
          <div class="fullscreen-content">
            <!-- 上方图标 -->
            <div class="fs-icon-wrap">
              <div class="fs-icon" :style="{ background: fullscreenCard.bgColor }">
                <img v-if="fullscreenCard.coverUrl" :src="fullscreenCard.coverUrl" alt="" class="word-cover fs-cover" />
                <span v-else class="fs-emoji">{{ fullscreenCard.icon }}</span>
              </div>
            </div>

            <!-- 英文大字 -->
            <h1 class="fs-en" @click="speakWord(fullscreenCard)">
              {{ fullscreenCard.en }}
            </h1>

            <!-- 中文翻译 -->
            <p class="fs-zh" :class="{ hidden: !showTranslation }">
              {{ fullscreenCard.zh }}
            </p>

            <!-- 发音提示 -->
            <p class="fs-hint">
              <font-awesome-icon icon="hand-pointer" class="hint-icon" />
              点击卡片即可发音
            </p>
          </div>

          <!-- 左右切换 -->
          <button class="nav-btn prev" @click.stop="prevCard">
            <font-awesome-icon icon="chevron-left" />
          </button>
          <button class="nav-btn next" @click.stop="nextCard">
            <font-awesome-icon icon="chevron-right" />
          </button>

          <!-- 进度 -->
          <div class="fs-progress">
            {{ currentIndex + 1 }} / {{ filteredCards.length }}
          </div>
        </div>
      </div>
    </Transition>

    <ParentPasswordModal ref="parentPasswordModalRef" />
  </div>
</template>

<script setup lang="ts">
import logo2Image from '../assets/logo2.png'
import menuBgImage from '../assets/menu_bg.jpg'
import ParentPasswordModal from '../components/ParentPasswordModal.vue'
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { getWordsList, type Word } from '@/api/course-word'
import { addHistory } from '@/api/history'
import { getAccessToken } from '@/config/axios/auth'

type WordCard = {
  id: number
  en: string
  zh: string
  icon: string
  bgColor: string
  coverUrl?: string
  audioUrl?: string
}

const isGuestUser = computed(() => getAccessToken() == null)
const route = useRoute()
const router = useRouter()

const activeNav = ref('')
const showTranslation = ref(true)
const isAutoPlaying = ref(false)
const fullscreenCard = ref<WordCard | null>(null)
const currentPlayingIndex = ref(-1)
const isHeaderScrolled = ref(false)
const pausedIndex = ref(-1) // 保存暂停时的索引
const PARENT_VERIFIED_STORAGE_KEY = 'tree-sprout-parent-verified'
const parentPasswordModalRef = ref(null)
const historyRecordedSeriesIds = new Set<number>()
let audioPlayer: HTMLAudioElement | null = null
let autoPlayTimer: ReturnType<typeof setInterval> | null = null

const wordList = ref<Word[]>([])

const navItems = [
  { id: 'home', label: '首页', icon: 'home' },
  { id: 'history', label: '观看历史', icon: 'play-circle' },
  { id: 'favorites', label: '宝贝收藏', icon: 'heart' },
  { id: 'settings', label: '偏好设置', icon: 'cog' },
  { id: 'parent', label: '家长模式', icon: 'user-shield' },
]


const letterBgColors = [
  'rgba(255, 140, 66, 0.15)',
  'rgba(91, 143, 249, 0.15)',
  'rgba(76, 175, 80, 0.15)',
  'rgba(255, 193, 7, 0.15)',
  'rgba(156, 39, 176, 0.15)',
  'rgba(0, 150, 136, 0.15)',
]

const getRouteSeriesId = () => {
  const rawSeriesId = Array.isArray(route.query.seriesId)
    ? route.query.seriesId[0]
    : route.query.seriesId
  const seriesId = Number(rawSeriesId)

  return Number.isFinite(seriesId) ? seriesId : undefined
}

const getRouteTitle = () => {
  const rawTitle = Array.isArray(route.query.title)
    ? route.query.title[0]
    : route.query.title
  const title = String(rawTitle ?? '').trim()

  if (!title) {
    return '26个英文字母'
  }

  try {
    return decodeURIComponent(title)
  } catch {
    return title
  }
}

const pageTitle = computed(() => getRouteTitle())

const getWordIcon = (english: unknown, index: number) => {
  const trimmedEnglish = String(english ?? '').trim()

  if (trimmedEnglish.length >= 2) {
    return trimmedEnglish.slice(0, 2)
  }

  if (trimmedEnglish.length === 1) {
    return `${trimmedEnglish}${trimmedEnglish.toLowerCase()}`
  }

  return String(index + 1)
}

const normalizeWordsResponse = (data: any): Word[] => {
  if (Array.isArray(data)) {
    return data
  }

  if (Array.isArray(data?.list)) {
    return data.list
  }

  return []
}

// 词卡数据
const recordWordHistory = (seriesId: number) => {
  if (!seriesId || historyRecordedSeriesIds.has(seriesId)) {
    return
  }
  if(!isGuestUser.value) {
    historyRecordedSeriesIds.add(seriesId)
    addHistory({
      contentType: 4,
      contentId: seriesId,
      seriesId
    }).catch(() => {
      historyRecordedSeriesIds.delete(seriesId)
    })
  }
}

const cards = computed<WordCard[]>(() => {
  return wordList.value.map((word, index) => {
    const english = word.english || ''

    return {
      id: word.id ?? index + 1,
      en: english,
      zh: word.chinese || '',
      icon: getWordIcon(english, index),
      bgColor: letterBgColors[index % letterBgColors.length],
      coverUrl: word.coverUrl,
      audioUrl: word.audioUrl,
    }
  })
})

// 过滤后的卡片
const filteredCards = computed(() => {
  return cards.value
})

// 当前卡片索引
const currentIndex = computed(() => {
  if (!fullscreenCard.value) return 0
  return filteredCards.value.findIndex(c => c.id === fullscreenCard.value.id)
})

// 关闭全屏
const closeFullscreen = () => {
  fullscreenCard.value = null
  stopAutoPlay()
}

// 上一张
const prevCard = () => {
  const idx = currentIndex.value
  const prev = (idx - 1 + filteredCards.value.length) % filteredCards.value.length
  fullscreenCard.value = filteredCards.value[prev]
  speakWord(fullscreenCard.value)
}

// 下一张
const nextCard = () => {
  const idx = currentIndex.value
  const next = (idx + 1) % filteredCards.value.length
  fullscreenCard.value = filteredCards.value[next]
  speakWord(fullscreenCard.value)
}

// 发音
const stopCurrentAudio = () => {
  if (!audioPlayer) {
    return
  }

  audioPlayer.pause()
  audioPlayer.currentTime = 0
  audioPlayer = null
}

const speakWord = (cardOrText: WordCard | string) => {
  const audioUrl = typeof cardOrText === 'string' ? '' : cardOrText.audioUrl
  const text = typeof cardOrText === 'string' ? cardOrText : cardOrText.en

  stopCurrentAudio()

  if (audioUrl) {
    if ('speechSynthesis' in window) {
      speechSynthesis.cancel()
    }

    const audio = new Audio(audioUrl)
    audioPlayer = audio
    audio.onended = () => {
      if (audioPlayer === audio) {
        audioPlayer = null
      }
      if (!isAutoPlaying.value) {
        currentPlayingIndex.value = -1
      }
    }
    audio.onerror = () => {
      if (audioPlayer === audio) {
        audioPlayer = null
      }
      playSpeechText(text)
    }
    audio.play().catch(() => {
      audioPlayer = null
      playSpeechText(text)
    })
    return
  }

  playSpeechText(text)
}

const playSpeechText = (text: string) => {
  if (!text || !('speechSynthesis' in window)) {
    return
  }

  speechSynthesis.cancel()
  const utterance = new SpeechSynthesisUtterance(text)
  utterance.lang = 'en-US'
  utterance.rate = 0.85
  utterance.onend = () => {
    if (!isAutoPlaying.value) {
      currentPlayingIndex.value = -1
    }
  }
  speechSynthesis.speak(utterance)
}

const getWordList = async () => {
  const seriesId = getRouteSeriesId()

  if (!seriesId) {
    wordList.value = []
    return
  }

  try {
    const data = await getWordsList(seriesId)
    wordList.value = normalizeWordsResponse(data)
    recordWordHistory(seriesId)
  } catch {
    wordList.value = []
  }
}

const handleGoBack = () => {
  if (window.history.length > 1) {
    router.back()
    return
  }

  router.push('/category')
}

const openParentPasswordModal = () => {
  parentPasswordModalRef.value?.open()
}

const handleNavItemClick = (item) => {
  if (item.id === 'home') {
    router.push('/')
    return
  }

  if (item.id === 'parent') {
    openParentPasswordModal()
    return
  }

  if (item.id === 'settings') {
    router.push('/preference')
    return
  }

  if (item.id === 'history' || item.id === 'favorites') {
    router.push({
      path: '/history',
      query: item.id === 'favorites' ? { tab: 'favorites' } : {}
    })
  }
}

// 滚动到当前播放的卡片
const scrollToCard = (el) => {
  if (el) {
    el.scrollIntoView({ behavior: 'smooth', block: 'center' })
  }
}

// 切换翻译显示
const toggleTranslation = () => {
  showTranslation.value = !showTranslation.value
}



// 跳转到全屏页面
const goToFullscreen = () => {
  router.push({
    path: '/fullscreen',
    query: {
      seriesId: route.query.seriesId
    }
  })
}

// 自动播放
const toggleAutoPlay = () => {
  if (isAutoPlaying.value) {
    stopAutoPlay()
  } else {
    startAutoPlay()
  }
}

const startAutoPlay = () => {
  if (filteredCards.value.length > 0) {
    // 如果有暂停记录，从暂停位置继续；否则从第一个开始
    if (pausedIndex.value >= 0) {
      currentPlayingIndex.value = pausedIndex.value
    } else {
      currentPlayingIndex.value = 0
    }
    
    const currentCard = filteredCards.value[currentPlayingIndex.value]
    speakWord(currentCard)
    isAutoPlaying.value = true
    
    // 设置定时器，每3秒播放下一个
    autoPlayTimer = setInterval(() => {
      const nextIndex = (currentPlayingIndex.value + 1) % filteredCards.value.length
      currentPlayingIndex.value = nextIndex
      const nextCardItem = filteredCards.value[nextIndex]
      speakWord(nextCardItem)
    }, 3000)
  }
}

const stopAutoPlay = () => {
  isAutoPlaying.value = false
  // 保存当前播放位置
  pausedIndex.value = currentPlayingIndex.value
  if (autoPlayTimer) {
    clearInterval(autoPlayTimer)
    autoPlayTimer = null
  }
  stopCurrentAudio()
  if ('speechSynthesis' in window) {
    speechSynthesis.cancel()
  }
}

// 占位功能
const openSettings = () => {
  alert('设置功能开发中...')
}

const openTestMode = () => {
  alert('测试模式开发中...')
}

// 键盘事件
const handleKeydown = (e) => {
  if (!fullscreenCard.value) return

  if (e.key === 'Escape') {
    closeFullscreen()
  } else if (e.key === 'ArrowLeft') {
    prevCard()
  } else if (e.key === 'ArrowRight') {
    nextCard()
  } else if (e.key === ' ') {
    e.preventDefault()
    speakWord(fullscreenCard.value)
  }
}

const handleCardClick = (card, index) => {
  currentPlayingIndex.value = index
  pausedIndex.value = index
  speakWord(card)
}

const handlePageScroll = () => {
  isHeaderScrolled.value = window.scrollY > 12
}

watch(
  () => route.query.seriesId,
  () => {
    stopAutoPlay()
    fullscreenCard.value = null
    currentPlayingIndex.value = -1
    pausedIndex.value = -1
    getWordList()
  }
)

onMounted(() => {
  getWordList()
  window.addEventListener('keydown', handleKeydown)
  window.addEventListener('scroll', handlePageScroll, { passive: true })
  handlePageScroll()
})

onUnmounted(() => {
  window.removeEventListener('keydown', handleKeydown)
  window.removeEventListener('scroll', handlePageScroll)
  stopAutoPlay()
})
</script>

<style scoped>
/* 基础变量 */
:root {
  --primary: #FF8C42;
  --bg: #F9F7F2;
  --text: #2D3436;
  --text-light: #666;
}

/* 页面容器 */
.card-page {
  --sidebar-width: 264px;
  min-height: 100vh;
  background: var(--bg);
  position: relative;
  overflow-x: hidden;
  padding-bottom: 100px;
  font-family: 'Nunito', 'Source Han Sans CN', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
}

/* 背景渐变 */
.bg-gradient {
  position: fixed;
  inset: 0;
  background: linear-gradient(135deg, #FFF8F0 0%, #F9F7F2 50%, #F5F0E8 100%);
  z-index: -3;
}

/* 光晕装饰 */
.orb {
  position: fixed;
  border-radius: 50%;
  filter: blur(100px);
  opacity: 0.6;
  z-index: -2;
  animation: float 8s ease-in-out infinite;
}

.orb-1 {
  width: 500px;
  height: 500px;
  background: linear-gradient(135deg, #FFE4D4, #FFD4C4);
  top: -150px;
  right: -100px;
}

.orb-2 {
  width: 400px;
  height: 400px;
  background: linear-gradient(135deg, #E8F4FF, #D4E8FF);
  bottom: -100px;
  left: -100px;
  animation-delay: -3s;
}

.orb-3 {
  width: 300px;
  height: 300px;
  background: linear-gradient(135deg, #FFEAA8, #FFD88A);
  top: 50%;
  left: 30%;
  transform: translateY(-50%);
  animation-delay: -5s;
  opacity: 0.4;
}

@keyframes float {
  0%, 100% { transform: translate(0, 0) scale(1); }
  33% { transform: translate(30px, -30px) scale(1.05); }
  66% { transform: translate(-20px, 20px) scale(0.95); }
}

.content-wrapper {
  display: block;
  width: 100%;
  min-height: 100vh;
}

.sidebar {
  width: 264px;
  flex-shrink: 0;
  height: 100vh;
  overflow: hidden;
  background: #FFF9F5;
  box-shadow: 4px 0 24px rgba(0, 0, 0, 0.06);
  position: fixed;
  top: 0;
  left: 0;
  bottom: 0;
  z-index: 30;
}

.nav-card {
  height: 100%;
  min-height: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  position: relative;
}

.nav-logo {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 20px 20px;
  border-bottom: 1px solid rgba(255, 138, 61, 0.15);
}

.nav-logo .logo-icon {
  width: 62px;
  height: 62px;
  background: #fff;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(140, 104, 68, 0.12);
}

.nav-logo .logo2-icon {
  display: block;
  width: 52px;
  height: 52px;
  object-fit: contain;
}

.brand-copy {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}

.nav-logo .brand-name {
  margin: 0;
  font-size: 24px;
  font-weight: 800;
  color: #2D3436;
  letter-spacing: 1px;
  line-height: 1.05;
}

.brand-subtitle {
  color: #a78b76;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 1.2px;
  line-height: 1;
}

.nav-list {
  list-style: none;
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  gap: clamp(12px, 2vh, 20px);
  padding: clamp(12px, 2vh, 16px) 12px;
}

.nav-item {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 18px;
  border-radius: 14px;
  color: #666;
  font-size: 15px;
  font-weight: 500;
  transition: all 0.3s ease;
  cursor: pointer;
  position: relative;
  text-decoration: none;
}

.nav-item:hover {
  background: rgba(255, 138, 61, 0.08);
  color: #FF8A3D;
}

.nav-item.active {
  background: linear-gradient(135deg, #FFE4D4 0%, #FFD4C4 100%);
  color: #FF6B35;
  font-weight: 700;
  box-shadow: 0 4px 16px rgba(255, 138, 61, 0.2);
}

.nav-icon {
  font-size: 18px;
  width: 24px;
  text-align: center;
}

.nav-menu-visual {
  flex: 1 1 auto;
  min-height: 0;
  width: 100%;
  margin-top: auto;
  border-radius: 0;
  overflow: hidden;
  pointer-events: none;
  display: flex;
  align-items: flex-end;
  justify-content: center;
}

.nav-menu-image {
  display: block;
  flex-shrink: 0;
  width: 100%;
  min-width: 100%;
  height: auto;
  object-fit: contain;
  object-position: center bottom;
}

/* 主内容区 */
.main-content {
  min-width: 0;
  width: calc(100% - var(--sidebar-width));
  max-width: none;
  margin: 0 0 0 var(--sidebar-width);
  padding: 60px 40px;
  position: relative;
  z-index: 1;
}

/* 1️⃣ 顶部 Header */
.page-header {
  position: relative;
  text-align: center;
  margin-bottom: 32px;
}

.page-header-spacer {
  display: none;
}

.mobile-back-btn {
  display: none;
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
  width: 44px;
  height: 44px;
  border: 1px solid rgba(255, 255, 255, 0.85);
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.82);
  backdrop-filter: blur(14px);
  -webkit-backdrop-filter: blur(14px);
  color: #5f5044;
  font-size: 16px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);
  cursor: pointer;
  align-items: center;
  justify-content: center;
}

.main-title {
  max-width: min(920px, 100%);
  margin: 0 auto;
  padding: 0 56px;
  font-size: 44px;
  font-weight: 900;
  color: var(--text);
  letter-spacing: 0;
  line-height: 1.12;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.main-title .highlight {
  color: var(--primary);
  position: relative;
  display: inline-block;
}

.main-title .highlight::after {
  content: '';
  position: absolute;
  bottom: 8px;
  left: 0;
  width: 100%;
  height: 12px;
  background: linear-gradient(135deg, rgba(255, 140, 66, 0.3), rgba(255, 107, 53, 0.2));
  border-radius: 6px;
  z-index: -1;
}

/* 3️⃣ 卡片列表区 */
.cards-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 230px));
  justify-content: center;
  gap: 24px;
}

/* 玻璃拟态卡片 */
.word-card {
  aspect-ratio: 6 / 7;
  background: transparent;
  border: 1px solid rgba(238, 240, 245, 0.9);
  border-radius: 22px;
  padding: 8px;
  cursor: pointer;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
  min-height: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  box-shadow: 0 14px 34px rgba(76, 82, 96, 0.08);
}

.word-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(255, 255, 255, 0.4),
    transparent
  );
  transition: left 0.6s ease;
}

.word-card:hover::before {
  left: 100%;
}

.word-card:hover {
  transform: translateY(-8px);
  box-shadow:
    0 22px 46px rgba(76, 82, 96, 0.13),
    0 8px 22px rgba(76, 82, 96, 0.08);
}

.word-card:hover .word-cover {
  transform: scale(1.04);
}

.word-card.playing {
  transform: translateY(-4px);
  box-shadow: 0 20px 48px rgba(255, 140, 66, 0.24);
  border: 2px solid #FF8C42;
  animation: pulse 1s ease-in-out infinite;
}

.word-card.playing .word-cover {
  transform: scale(1.03);
}

@keyframes pulse {
  0%, 100% {
    box-shadow: 0 20px 60px rgba(255, 140, 66, 0.3);
  }
  50% {
    box-shadow: 0 20px 60px rgba(255, 140, 66, 0.5);
  }
}

.word-cover {
  display: block;
  width: 100%;
  height: 100%;
  border-radius: inherit;
  object-fit: cover;
  transition: transform 0.35s ease;
}

.word-card > .word-cover {
  border-radius: 16px;
  object-fit: cover;
}

/* 5️⃣ 底部 Dock 控制栏 */
.dock-bar {
  position: fixed;
  bottom: 15px;
  left: calc(280px + (100vw - 280px) / 2);
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 14px;
  padding: 0 34px;
  width: 720px;
  height: 86px;
  background: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.9);
  border-radius: 9999px;
  z-index: 9999;
}

.dock-bar > .dock-item {
  flex: 1;
  min-width: 0;
}

.dock-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 12px 18px;
  background: transparent;
  border: none;
  border-radius: 16px;
  color: var(--text-light);
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
}

.dock-item svg {
  font-size: 19px;
}

.dock-item:hover {
  background: rgba(0, 0, 0, 0.05);
  color: var(--text);
}

.dock-item.active {
  color: var(--primary);
  background: rgba(255, 140, 66, 0.1);
}

.dock-item.main-btn {
  width: 60px;
  height: 60px;
  flex: 0 0 60px;
  background: linear-gradient(135deg, #FF8C42, #FF6B35);
  color: white;
  border-radius: 50%;
  padding: 0;
  margin: 0 6px;
  box-shadow: 0 6px 20px rgba(255, 140, 66, 0.34);
  font-size: 20px;
  justify-content: center;
}

.dock-item.main-btn svg {
  font-size: 21px;
}

.dock-item.main-btn:hover {
  transform: scale(1.08);
  box-shadow: 0 12px 40px rgba(255, 140, 66, 0.5);
}

.dock-item.main-btn:active {
  transform: scale(0.95);
}

/* 4️⃣ 全屏沉浸模式 */
.fullscreen-overlay {
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(253, 251, 247, 0.92);
  backdrop-filter: blur(40px);
  -webkit-backdrop-filter: blur(40px);
}

.fullscreen-card {
  position: relative;
  width: 90vw;
  max-width: 800px;
  height: 85vh;
  max-height: 700px;
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(30px);
  border: 1px solid rgba(255, 255, 255, 0.9);
  border-radius: 60px;
  box-shadow: 0 40px 100px rgba(0, 0, 0, 0.15);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  animation: breathe 3s ease-in-out infinite;
}

@keyframes breathe {
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.01); }
}

.fullscreen-bg {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 50%;
  opacity: 0.3;
  border-radius: 60px 60px 0 0;
}

.close-btn {
  position: absolute;
  top: 24px;
  right: 24px;
  width: 56px;
  height: 56px;
  background: rgba(255, 255, 255, 0.8);
  border: 1px solid rgba(255, 255, 255, 0.9);
  border-radius: 50%;
  color: var(--text-light);
  font-size: 22px;
  cursor: pointer;
  transition: all 0.3s ease;
  z-index: 10;
}

.close-btn:hover {
  background: white;
  color: var(--text);
  transform: scale(1.1);
}

.fullscreen-content {
  position: relative;
  z-index: 5;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

.fs-icon-wrap {
  margin-bottom: 30px;
}

.fs-icon {
  width: 140px;
  height: 140px;
  border-radius: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: iconBounce 0.6s ease;
}

.fs-emoji {
  font-size: 72px;
}

.fs-cover {
  border-radius: 40px;
}

@keyframes iconBounce {
  0% { transform: scale(0.5); opacity: 0; }
  50% { transform: scale(1.1); }
  100% { transform: scale(1); opacity: 1; }
}

.fs-en {
  font-size: 96px;
  font-weight: 900;
  color: var(--text);
  letter-spacing: 6px;
  margin: 20px 0;
  cursor: pointer;
  transition: transform 0.2s ease;
}

.fs-en:hover {
  transform: scale(1.05);
}

.fs-zh {
  font-size: 36px;
  color: var(--text-light);
  margin-bottom: 30px;
  transition: opacity 0.3s ease;
}

.fs-zh.hidden {
  opacity: 0;
}

.fs-hint {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 16px;
  color: #AAA;
  animation: fadeInUp 0.5s ease 0.3s both;
}

.hint-icon {
  animation: point 1s ease-in-out infinite;
}

@keyframes point {
  0%, 100% { transform: translateX(0); }
  50% { transform: translateX(8px); }
}

@keyframes fadeInUp {
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
}

.nav-btn {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 64px;
  height: 64px;
  background: rgba(255, 255, 255, 0.9);
  border: 1px solid rgba(255, 255, 255, 0.9);
  border-radius: 50%;
  color: var(--text-light);
  font-size: 24px;
  cursor: pointer;
  transition: all 0.3s ease;
  z-index: 10;
}

.nav-btn:hover {
  background: white;
  color: var(--primary);
  transform: translateY(-50%) scale(1.1);
}

.nav-btn.prev { left: 30px; }
.nav-btn.next { right: 30px; }

.fs-progress {
  position: absolute;
  bottom: 30px;
  left: 50%;
  transform: translateX(-50%);
  font-size: 18px;
  font-weight: 600;
  color: var(--text-light);
}

/* 全屏过渡动画 */
.fullscreen-enter-active {
  animation: zoomIn 0.3s ease;
}

.fullscreen-leave-active {
  animation: zoomOut 0.3s ease;
}

@keyframes zoomIn {
  from { opacity: 0; transform: scale(0.9); }
  to { opacity: 1; transform: scale(1); }
}

@keyframes zoomOut {
  from { opacity: 1; transform: scale(1); }
  to { opacity: 0; transform: scale(0.9); }
}

/* 响应式 */
@media (max-width: 1200px) {
  .card-page {
    --sidebar-width: 224px;
  }

  .sidebar {
    width: 224px;
  }

  .nav-logo {
    padding: 20px 20px;
    gap: 16px;
  }

  .nav-logo .logo-icon {
    width: 62px;
    height: 62px;
    font-size: 20px;
  }

  .nav-logo .logo2-icon {
    width: 52px;
    height: 52px;
  }

  .nav-logo .brand-name {
    font-size: 24px;
  }

  .nav-logo .brand-subtitle {
    font-size: 12px;
  }

  .nav-item {
    padding: 16px;
  }

  .dock-bar {
    left: calc(224px + (100vw - 224px) / 2);
  }

  .cards-grid {
    grid-template-columns: repeat(3, minmax(0, 220px));
    gap: 22px;
  }

  .main-title {
    font-size: 40px;
  }
}

@media (max-width: 992px) {
  .card-page {
    --sidebar-width: 208px;
  }

  .sidebar {
    width: 208px;
  }

  .dock-bar {
    left: calc(208px + (100vw - 208px) / 2);
  }

  .cards-grid {
    grid-template-columns: repeat(2, minmax(0, 220px));
    gap: 20px;
  }

  .main-content {
    padding: 40px 24px 120px;
  }

  .page-header {
    margin-bottom: 28px;
  }

  .main-title {
    font-size: 36px;
  }

  .fs-en {
    font-size: 72px;
  }

  .fs-zh {
    font-size: 28px;
  }
}

@media (max-width: 768px), (min-width: 769px) and (max-width: 1024px) and (orientation: portrait) {
  .card-page {
    --sidebar-width: 0px;
  }

  .content-wrapper {
    display: block;
    min-height: 100vh;
  }

  .sidebar {
    display: none;
  }

  .main-content {
    width: 100%;
    margin-left: 0;
    padding: 0 16px 120px;
  }

  .page-header {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 20;
    margin-bottom: 0;
    min-height: 56px;
    padding: calc(env(safe-area-inset-top, 0px) + 6px) 16px 6px;
    border-radius: 0;
    transition: background-color 0.2s ease, box-shadow 0.2s ease;
  }

  .page-header.is-scrolled {
    background: rgba(249, 247, 242, 0.96);
    box-shadow: 0 8px 20px rgba(45, 52, 54, 0.08);
  }

  .page-header-spacer {
    display: block;
    height: calc(env(safe-area-inset-top, 0px) + 72px);
  }

  .mobile-back-btn {
    display: inline-flex;
    left: 16px;
  }

  .main-title {
    padding: 0 56px;
    font-size: 20px;
  }

  .cards-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 18px 12px;
  }

  .word-card {
    border-radius: 18px;
  }

  .dock-bar {
    left: 50%;
    padding: 10px 16px;
    gap: 6px;
    bottom: 20px;
  }

  .dock-item {
    padding: 8px 10px;
    font-size: 10px;
  }

  .dock-item span {
    display: none;
  }

  .dock-item.main-btn {
    width: 46px;
    height: 46px;
    flex: 0 0 46px;
    margin: 0 4px;
    font-size: 16px;
  }

  .dock-item.main-btn svg {
    font-size: 17px;
  }

  .fullscreen-card {
    width: 95vw;
    height: 90vh;
    border-radius: 40px;
  }

  .fs-en {
    font-size: 52px;
  }

  .fs-zh {
    font-size: 24px;
  }

  .fs-icon {
    width: 100px;
    height: 100px;
    border-radius: 28px;
  }

  .fs-emoji {
    font-size: 52px;
  }

  .nav-btn {
    width: 48px;
    height: 48px;
    font-size: 18px;
  }

  .nav-btn.prev { left: 12px; }
  .nav-btn.next { right: 12px; }
}

/* 移动端适配 */
@media (max-width: 576px) {
  .dock-bar {
    width: 95%;
    height: 68px;
    bottom: 15px;
    gap: 4px;
    padding: 0 10px;
    border-radius: 34px;
  }

  .dock-item {
    padding: 4px 3px;
    font-size: 10px;
    flex: 1;
  }

  .dock-item svg {
    font-size: 18px;
  }

  .dock-item span {
    display: block;
    font-size: 9px;
    margin-top: 2px;
  }

  .dock-item.main-btn {
    width: 48px;
    height: 48px;
    margin: 0 4px;
    font-size: 16px;
    flex: 0 0 auto;
  }

  .dock-item.main-btn svg {
    font-size: 17px;
  }
}
@media (min-width: 769px) {
  .nav-logo { padding: 20px 20px; gap: 16px; }
  .nav-logo .logo-icon { width: 62px; height: 62px; }
  .nav-logo .logo2-icon { width: 52px; height: 52px; }
  .nav-logo .brand-name { font-size: 24px; letter-spacing: 1px; line-height: 1.05; }
  .nav-logo .brand-subtitle { font-size: 12px; letter-spacing: 1.2px; line-height: 1; }
  .nav-item { padding: clamp(12px, 1.8vh, 18px) 18px; }
}

@media (min-width: 993px) and (max-width: 1200px) {
  .sidebar { width: 224px; }
}

@media (min-width: 769px) and (max-width: 992px) {
  .sidebar { width: 208px; }
}
</style>
